import React, { useId, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { CompanyCard } from "../../../common/u1/CompanyCard";
import { CompanyUpdates } from "./CompanyUpdates";
import { useCompanyUpdates } from "./useCompanyUpdates";
import { useUpdateViewedTimestampMutation } from "../../../../store/Company/companyApiSlice";
import { cn } from "../../../common/u1/cn";
import { ChevronDown, BellIcon } from "../../../../assets/ui/icons";

/* One followed business: the shared company card, with its recent changes on a strip beneath it. */
export const FollowedCompanyRow = ({ follow }) => {
  const company = follow?.companyID;
  const { token } = useSelector((state) => state.auth);
  const [updateViewedTimestamp] = useUpdateViewedTimestampMutation();
  const [open, setOpen] = useState(false);
  const panelId = `${useId()}-updates`;

  /* When these updates were read, for this row, in this session. */
  const [seenAt, setSeenAt] = useState(null);
  const openedAt = useRef(null);

  const { updates, total, newCount } = useCompanyUpdates(
    company?.companyID,
    follow,
    seenAt
  );

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) {
      openedAt.current = new Date().toISOString();
      /* Transcribed from FollowPopup's mount effect: looking at the changes is
         what marks them seen. Fired only on open, not on close. */
      if (follow?.company_follow_ID) {
        updateViewedTimestamp({ followID: follow.company_follow_ID, token });
      }
      return;
    }
    /* Closed again — the reader has had them, so the strip catches up. */
    if (openedAt.current) setSeenAt(openedAt.current);
  };

  const label =
    newCount > 0
      ? `${newCount} new profile update${newCount === 1 ? "" : "s"}`
      : `${total} profile update${total === 1 ? "" : "s"}`;

  return (
    <div className="overflow-hidden rounded-xl border border-[var(--line)] bg-white">
      {/* The card draws its own border; inside this shell it would be a second
          rule, so it is suppressed. */}
      <CompanyCard
        company={company}
        actionLabel="View more"
        actionAriaLabel={`View more about ${company?.registered_company_name || company?.name}`}
        /* Transcribed: the live card's link carries target="_blank". */
        onAction={(c) => window.open(`/search/${c?.companyID}`, "_blank")}
        className="rounded-none border-0"
      />

      {total > 0 ? (
        <>
          <button
            type="button"
            onClick={toggle}
            aria-expanded={open}
            aria-controls={panelId}
            className={cn(
              "flex w-full items-center gap-2.5 border-t border-[var(--line)] px-4 py-3 text-left",
              newCount > 0 ? "bg-[var(--unread-bg)]" : "bg-[var(--bg)]"
            )}
          >
            <span className="shrink-0 text-[var(--coral)]">
              <BellIcon size={16} />
            </span>
            <span className="text-[13.5px] font-semibold text-[var(--ink)] font-sans">
              {label}
            </span>
            {newCount > 0 && (
              <span className="flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-[var(--coral)] px-1 text-[11px] font-bold text-white font-sans">
                {newCount}
              </span>
            )}
            <span className="ml-auto shrink-0 text-[var(--muted)]">
              <ChevronDown open={open} />
            </span>
          </button>

          {open && (
            <div className="border-t border-[var(--line)]">
              <CompanyUpdates
                id={panelId}
                updates={updates}
                viewedAt={seenAt || follow?.viewed_timestamp}
              />
            </div>
          )}
        </>
      ) : (
        /* A company with nothing to report says so rather than ending on the card. */
        <div className="flex w-full items-center gap-2.5 border-t border-[var(--line)] bg-[#fff] px-4 py-3">
          <span className="shrink-0 text-[var(--muted-2)]">
            <BellIcon size={16} />
          </span>
          <span className="text-[13.5px] font-medium text-[var(--muted)] font-sans">
            No recent updates
          </span>
        </div>
      )}
    </div>
  );
};

export default FollowedCompanyRow;
